import React, {
    type Dispatch,
    type SetStateAction, useCallback, useContext, useState,
} from 'react';

import {ErrorFlatButton} from 'src/components/error-flat-button';
import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {FormItemSkeleton, Skeleton, SkeletonItem} from 'src/components/skeleton';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, type AccessRight} from 'src/hooks/access-rights/constants';
import {validateMicrositeHost} from 'src/openapi/admin/apis/MicroSitesBaseInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import {FlatButton} from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';

import {
    MICROSITE_DOMAINS_CONTROL,
    MICROSITE_DOMAINS_DUPLICATE,
    MICROSITE_DOMAINS_DUPLICATE_MESSAGE,
    MICROSITE_DOMAINS_EXISTS,
} from './constants';

import type {MicroSiteBaseInfo} from '../../typedef';
import type {Error} from 'src/openapi/admin/models/Error';


type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    microSiteBaseInfo: MicroSiteBaseInfo,
    initMicroSiteBaseInfo: MicroSiteBaseInfo,
    setMicroSiteBaseInfo: Dispatch<SetStateAction<MicroSiteBaseInfo>>,
    trackChange: (parameter: string) => void,
    readonly?: boolean,
};

export const MicroSiteHostsSection = (props: Props) => {
    const {
        isLoading,
        loadingError,
        microSiteBaseInfo,
        initMicroSiteBaseInfo,
        setMicroSiteBaseInfo,
        readonly,
        trackChange,
    } = props;

    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);
    const [checkingMicroSites, setCheckingMicroSites] = useState<number[]>([]);

    const changeDomain = useCallback(async (domain: string, index: number) => {
        trackChange('Change Microsite Domains');
        const updatedDomains = microSiteBaseInfo.domains.map((dom, i) => (
            i === index ? domain : dom),
        );
        if (domain && updatedDomains.filter(updatedDomain => updatedDomain === domain).length > 1) {
            addError({
                controlId: `${MICROSITE_DOMAINS_CONTROL}-${index}`,
                code: MICROSITE_DOMAINS_DUPLICATE,
                message: MICROSITE_DOMAINS_DUPLICATE_MESSAGE,
            });
        } else {
            updatedDomains.forEach((validationCandidate, id) => {
                if (updatedDomains.filter(updatedDomain => updatedDomain === validationCandidate).length === 1) {
                    removeError(`${MICROSITE_DOMAINS_CONTROL}-${id}`, [MICROSITE_DOMAINS_DUPLICATE]);
                }
            });
            const prevDomain = microSiteBaseInfo.domains[index];
            if (prevDomain !== domain) {
                microSiteBaseInfo.domains.forEach((validationCandidate, id) => {
                    if (prevDomain === validationCandidate) {
                        removeError(`${MICROSITE_DOMAINS_CONTROL}-${id}`, [MICROSITE_DOMAINS_DUPLICATE]);
                    }
                });
            }
        }

        removeError(`${MICROSITE_DOMAINS_CONTROL}-${index}`, [MICROSITE_DOMAINS_EXISTS]);
        if (!initMicroSiteBaseInfo.domains.includes(domain) && domain) {
            setCheckingMicroSites(prevState => [...prevState, index]);
            const serverValidation = await apiDispatch(validateMicrositeHost({host: domain}));
            setCheckingMicroSites(prevState => prevState.filter(i => i !== index));

            if (serverValidation.payload.error) {
                addError({
                    controlId: `${MICROSITE_DOMAINS_CONTROL}-${index}`,
                    code: MICROSITE_DOMAINS_EXISTS,
                    message: serverValidation.payload.data.message || 'Invalid domain',
                });
            }
        }

        setMicroSiteBaseInfo(state => ({...state, domains: updatedDomains}));
    }, [
        microSiteBaseInfo.domains,
        initMicroSiteBaseInfo.domains,
        addError, removeError,
        apiDispatch,
        setMicroSiteBaseInfo,
        trackChange,
        setCheckingMicroSites,
    ]);

    const removeDomain = useCallback((index: number) => {
        trackChange('Remove Microsite Domain');
        setMicroSiteBaseInfo(state => ({
            ...state,
            domains: state.domains.filter((_, i) => i !== index),
        }));
        removeError(`${MICROSITE_DOMAINS_CONTROL}-${index}`);
    }, [removeError, setMicroSiteBaseInfo, trackChange]);

    const addDomain = useCallback(() => {
        trackChange('Add Microsite Domain');
        setMicroSiteBaseInfo(state => ({
            ...state,
            domains: [...state.domains, ''],
        }));
    }, [setMicroSiteBaseInfo, trackChange]);

    return (
        <Skeleton
            shown={isLoading}
            view={(
                <FormItemSkeleton labelWidth={106}>
                    <SkeletonItem width={300} height={30}/>
                </FormItemSkeleton>
            )}>
            <ServerResponseHandler
                smallView={true}
                sectionName="Microsite Domains"
                accessRight={ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW as AccessRight}
                serverError={loadingError}>
                <div style={{width: 400}}>
                    <Label>
                        <RequiredMark label="Microsite Domains"/>
                    </Label>
                    <Group direction="v" alignItems="start" gap="xs">
                        {microSiteBaseInfo.domains.map((domain, index) => (
                            <Group key={index} alignItems="start">
                                <CustomValidatedControl name={`${MICROSITE_DOMAINS_CONTROL}-${index}`}>
                                    {error => (
                                        <FormInput
                                            width={300}
                                            placeholder="Domain URL"
                                            value={domain}
                                            busy={checkingMicroSites.includes(index)}
                                            readOnly={readonly}
                                            onBlur={value => changeDomain(value.trim(), index)}
                                            valid={error ? false : undefined}
                                            errorDescription={error}
                                        />
                                    )}
                                </CustomValidatedControl>
                                {microSiteBaseInfo.domains.length > 1 && !readonly && (
                                    <Item className="pt-xs">
                                        <ErrorFlatButton
                                            icon={Icons.TIMES_CIRCLE}
                                            label="Remove"
                                            onClick={() => removeDomain(index)}
                                        />
                                    </Item>
                                )}
                            </Group>
                        ))}
                        <FlatButton
                            disabled={readonly || microSiteBaseInfo.domains.includes('')}
                            onClick={addDomain}
                            startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}>
                            <b>Add Domain</b>
                        </FlatButton>
                    </Group>
                </div>
            </ServerResponseHandler>
        </Skeleton>
    );
};
